import { useState } from "react";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";

// Assets
import classes3 from "../../components/PDFUploader.module.css";
import deleteIcon from "../../assets/circled_cancel.svg";
import uploadIcon from "../../assets/upload_icon.png";
import closeIcon from "../../assets/close_icon.png";
import analyzeDoc from "../../assets/analyze-doc.png";
import { useTranslation } from "react-i18next";
import { DoneIcon, WarningIcon } from "../../assets/registtyIcons/icons";
import { useNavigate } from "react-router-dom";
import SectionDivider from "../../components/SectionDivider";
import { analyzeInvoiceForBank } from "../../services/bank/banksAnalyzeService";
import LoaderWithProgress from "../../components/LoaderWithProgress";

const UploadApplicationsDocumentsModal = ({
  isOpen,
  onClose,
  onSave,
  title = "uploadApplicationsDocuments",
  modalSize = "medium",
  applicationId = null,
  onAnalysisComplete = null,
}) => {
  // HOOKS
  const { t } = useTranslation("general");
  const navigate = useNavigate();

  // STATES
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState({
    invoice: null,
    bankForm: null,
    iraqiCompanyCertificate: null,
    supplierCertificate: null,
  });

  // Handlers
  const handleClose = () => {
    setFiles({
      invoice: null,
      bankForm: null,
      iraqiCompanyCertificate: null,
      supplierCertificate: null,
    });
    setError(null);
    onClose();
  };

  const handleFileChange = (fieldName) => (event) => {
    const file = event.target.files[0];
    setFiles((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    // Clear error when user selects a file
    if (error) setError(null);
  };

  const handleDeleteFile = (fieldName) => {
    setFiles((prev) => ({
      ...prev,
      [fieldName]: null,
    }));

    // Reset the file input value using direct DOM access
    const fileInput = document.getElementById(fieldName);
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Modified validation: invoice is required + at least one other file
      if (!files.invoice) {
        setError("invoiceFileRequired");
        setLoading(false);
        return;
      }

      const formData = new FormData();

      Object.keys(files).forEach((fieldName) => {
        if (files[fieldName]) {
          formData.append(fieldName, files[fieldName]);
        }
      });

      const response = await analyzeInvoiceForBank(formData);

      if (response.status === 200) {
        handleClose();
        if (applicationId && onAnalysisComplete) {
          // Re-analysis mode: pass data back to parent
          onAnalysisComplete(response.data);
        } else {
          // New analysis: navigate as before
          navigate("/bank-analyze-invoice", {
            state: { data: response.data },
          });
        }
      }
    } catch (err) {
      setError(err.message || "An error occurred while saving");
    } finally {
      setLoading(false);
    }
  };

  const renderFileUpload = (fieldName, labelKey) => (
    <div className="o-column u-margin--top_200">
      <SectionDivider title={t(labelKey)} noMargin={true} />

      <div className="o-column o-column--gap-size_100 u-padding_0">
        <div className="u-width--max-width_300">
          <input
            type="file"
            id={fieldName}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={handleFileChange(fieldName)}
            className={`${classes3.fileInput} u-padding_0`}
          />
          <div>
            <label htmlFor={fieldName}>
              <p className={` ${classes3.customFileInput}`}>
                <img src={uploadIcon} alt={`Upload image`} />
                <span>{t("uploadDocument")}</span>
              </p>
            </label>
          </div>

          <div className="u-width--max-width_300 o-row o-row--vertical-align_center">
            <div className="o-row o-row--vertical-align_center">
              {files[fieldName] ? (
                <span className="">
                  <DoneIcon width={30} height={30} />
                  {/* {t("fileSelected")} */}
                </span>
              ) : (
                <span className="u-color_success u-font-size_small o-row o-row--vertical-align_center o-row--gap-size_200">
                  <WarningIcon width={20} height={20} />
                  {t("noFileSelected")}
                </span>
              )}
            </div>
            {files[fieldName] && (
              <div className={classes3.fileName}>
                {t("fileName")} : {files[fieldName]?.name?.slice(0, 30)}
                <button
                  className={classes3.deleteFileButton}
                  onClick={() => handleDeleteFile(fieldName)}
                >
                  <img src={deleteIcon} className="u-height-specific_20" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      title={t(title)}
      onClose={handleClose}
      modalSize={modalSize}
      error={t(error)}
      footer={
        <>
          {loading && <LoaderWithProgress loadingText="analyzingFiles" />}
          <section className="o-row o-row--gap-size_300 o-row--horizontal-align_end">
            <CustomButton
              title={t("cancel")}
              onClick={handleClose}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
            <CustomButton
              title={t("analyze")}
              onClick={handleSave}
              icon={analyzeDoc}
              iconSize={20}
              disabled={loading}
            />
          </section>
        </>
      }
    >
      <div className="o-column o-column--gap-size_300">
        {error && <ErrorBox>{t(error)}</ErrorBox>}

        {/* File Upload Fields */}
        <div className="o-column o-column--gap-size_200">
          {renderFileUpload("invoice", "invoice")}
          {renderFileUpload("bankForm", "bankRequestFile")}
          {renderFileUpload(
            "iraqiCompanyCertificate",
            "iraqiCompanyCertificate"
          )}
          {renderFileUpload("supplierCertificate", "supplierCertificate")}
          {renderFileUpload("articlesOfAssociation", "articlesOfAssociation")}
        </div>
      </div>
    </Modal>
  );
};

export default UploadApplicationsDocumentsModal;
